import { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Controller, useForm } from "react-hook-form"
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material"
import {
  useGetAvailableShippingRatesMutation,
  useLazyGetCheckoutQuery,
  useUpdateCheckoutAttributesMutation,
  useUpdateShippingLineMutation,
} from "src/features/checkout/storefrontApi"
import {
  aboveMinimumRateSelector,
  amountForFreeShippingSelector,
  minimumRateSelector,
  setCheckout,
  shouldAddOrUpdateHawaiiGetSelector,
  hasBeerOrSeltzerItemsSelector,
  setCart,
} from "src/features/checkout/checkoutSlice"
import { DELIVERY_OPTIONS } from "src/app/constants"
import { currencyFormat, getCheckoutNoteDisplayValue } from "src/app/utils"

import BackAndNextButton from "./common/BackAndNextButton"
import { useLocation, useNavigate } from "react-router-dom"
import Addons from "./addons/Addons"
import { useAddHawaiiGetMutation } from "src/features/checkout/checkoutApi"
import analytics from "src/app/analytics"
import { useCartNoteUpdateMutation, useCartSelectedDeliveryOptionsUpdateMutation, useLazyCartGetAvailableShippingRatesQuery, useLazyGetCartQuery } from "src/features/checkout/storefrontCartApi"
import TextareaWithCharCount from "./common/CharacterCountTextarea"

function BottomNav({ loading, disabled }) {
  return (
    <BackAndNextButton
      loading={loading}
      disabled={disabled}
      // backButtonLabel="Return to information"
      backButtonPath="../information"
      nextButtonLabel="Continue to schedule"
      nextButtonPath="../schedule"
      nextButtonType="submit"
    />
  )
}

export default function Delivery() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const cart = useSelector((state) => state.checkout.cart)
  const aboveMinimumRate = useSelector(aboveMinimumRateSelector)
  const minimumRate = useSelector(minimumRateSelector)
  const amountForFreeShipping = useSelector(amountForFreeShippingSelector)
  const shouldAddOrUpdateHawaiiGet = useSelector(shouldAddOrUpdateHawaiiGetSelector)
  const hasBeerItems = useSelector(hasBeerOrSeltzerItemsSelector)
  const options = useSelector((state) => state.checkout.options)
  const deliveryOption = options.deliveryOption

  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      shippingRateHandle: cart.deliveryGroups?.edges[0]?.node.selectedDeliveryOption?.handle || "",
      note: getCheckoutNoteDisplayValue(cart.note || ""),
    },
  })

  const [
    getCart,
    { data: getCartData, isLoading: isGetCartLoading },
  ] = useLazyGetCartQuery()

  const [
    getAvailableShippingRates,
    {
      data: getAvailableShippingRatesData,
      isLoading: getAvailableShippingRatesLoading,
    },
  ] = useLazyCartGetAvailableShippingRatesQuery()

  const [
    cartSelectedDeliveryOptionsUpdate,
    { data: cartSelectedDeliveryOptionsUpdateData, isLoading: cartSelectedDeliveryOptionsUpdateLoading }
  ] = useCartSelectedDeliveryOptionsUpdateMutation()

  const [
    cartNoteUpdate,
    {
      data: cartNoteUpdateData,
      isLoading: cartNoteUpdateLoading,
    },
  ] = useCartNoteUpdateMutation()

  const [
    addHawaiiGet,
    {
      isLoading: hawaiiGetLoading,
    }
  ] = useAddHawaiiGetMutation()

  const onSubmit = async ({ shippingRateHandle, note }) => {
    await cartNoteUpdate({
      cartId: cart.id,
      note,
    })

    if (note?.trim().length) {
      analytics.track("add_note", {
        cart_id: cart.id,
        note,
      })
    }

    analytics.track("select_delivery_method", {
      cart_id: cart.id,
      handle: shippingRateHandle,
    })

    // Set/update shipping rate if needed.
    // if (checkout.shippingLine?.handle !== shippingRateHandle) {
    const deliveryGroupId = getAvailableShippingRatesData.deliveryGroups?.edges[0]?.node.id;
    console.log('cartSelectedDeliveryOptionsUpdate', {
      cartId: cart.id,
      selectedDeliveryOptions: [{
        deliveryGroupId: deliveryGroupId,
        deliveryOptionHandle: shippingRateHandle
      }],
      getAvailableShippingRatesData
    })
    await cartSelectedDeliveryOptionsUpdate({
      cartId: cart.id,
      selectedDeliveryOptions: [{
        deliveryGroupId: deliveryGroupId,
        deliveryOptionHandle: shippingRateHandle
      }],
    })
    // }

    if (shouldAddOrUpdateHawaiiGet) {
      await addHawaiiGet({
        checkoutId: cart.id
      })
    }

    await getCart(cart.id, false)

    navigate("../schedule", {
      state: {
        previousOptions: options,
      },
    })
  }

  useEffect(() => {
    if (!cart.buyerIdentity.deliveryAddressPreferences?.[0]) {
      navigate(-1)
      return
    }

    document.title = (
      [DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING, DELIVERY_OPTIONS.US_MAINLAND_SHIPPING]
        .includes(deliveryOption) ? "Shipping" : "Delivery"
    ) + " - Kakaako Wine - Checkout"

    getAvailableShippingRates(cart.id)

    analytics.track("page_view", {
      cart_id: cart.id,
      page_title: "Delivery",
      page_location: window.location.href,
    })
  }, [])

  useEffect(() => {
    // Fetch fresh shipping rates if lineItems has been updated.
    getAvailableShippingRates(cart.id)
  }, [cart.lines])

  useEffect(() => {
    if (cartSelectedDeliveryOptionsUpdateData?.cartSelectedDeliveryOptionsUpdate?.cart) {
      dispatch(setCart(cartSelectedDeliveryOptionsUpdateData.cartSelectedDeliveryOptionsUpdate.cart))
    }
  }, [cartSelectedDeliveryOptionsUpdateData]);

  useEffect(() => {
    if (cartNoteUpdateData?.cartNoteUpdate?.cart) {
      dispatch(setCart(cartNoteUpdateData.cartNoteUpdate.cart))
    }
  }, [cartNoteUpdateData])

  useEffect(() => {
    console.log('getCartData', getCartData)
    if (getCartData?.cart) {
      dispatch(setCart(getCartData.cart))
    }
  }, [getCartData])

  useEffect(() => {
    if (getAvailableShippingRatesData) {
      console.log('getAvailableShippingRatesData', getAvailableShippingRatesData);
      if (
        !getAvailableShippingRatesData?.cart?.deliveryGroups?.edges?.length > 0
      ) {
        // Refetch if checkout or availableShippingRates is not yet ready.
        getAvailableShippingRates(cart.id)
      } else {
        dispatch(setCart(getAvailableShippingRatesData.cart))

        // Find the best rate (cheapest price).
        let bestRate = null
        let cheapestPrice = Infinity

        for (const rate of getAvailableShippingRatesData.cart
          .deliveryGroups.edges[0].node.deliveryOptions) {
          const amount = parseFloat(rate.estimatedCost.amount)

          if (amount < cheapestPrice) {
            bestRate = rate
            cheapestPrice = amount
          }
        }

        if (bestRate) {
          // Automatically select the best rate.
          setValue("shippingRateHandle", bestRate.handle)
          const deliveryGroupId = getAvailableShippingRatesData.cart.deliveryGroups.edges[0].node.id;
          cartSelectedDeliveryOptionsUpdate({
            cartId: cart.id,
            selectedDeliveryOptions: [{
              deliveryGroupId: deliveryGroupId,
              deliveryOptionHandle: bestRate.handle
            }],
          }).then(() => {
            if (shouldAddOrUpdateHawaiiGet) {
              addHawaiiGet({
                checkoutId: cart.id,
              }).then(() => getCart(cart.id, false))
            }
          })
        }
      }
    }
  }, [getAvailableShippingRatesData])

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography component="h2" variant="h5" mb={1}>
        {[DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING, DELIVERY_OPTIONS.US_MAINLAND_SHIPPING].includes(deliveryOption)
          ? "Shipping"
          : "Delivery"}{" "}
        Fee
      </Typography>

      <Box>
        {getAvailableShippingRatesLoading ||
        !getAvailableShippingRatesData?.cart?.deliveryGroups?.edges?.length > 0 ? (
          <Box
            component={Paper}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            padding={2}
          >
            <CircularProgress />
            <Typography variant="body2" mt={1}>
              Getting available shipping rates...
            </Typography>
          </Box>
        ) : (
          <Box component={Paper}>
            {getAvailableShippingRatesData?.cart?.deliveryGroups?.edges?.length > 0 ? <>
              {aboveMinimumRate ? (
                <>
                  {amountForFreeShipping > 0 && (
                    <Alert
                      severity="error"
                      action={<Link href="/">Back to shop</Link>}
                      sx={{
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                      }}
                    >
                      Just {currencyFormat(amountForFreeShipping)} more for free
                      delivery!
                    </Alert>
                  )}
                  <Box padding={2}>
                    <Controller
                      rules={{ required: true }}
                      control={control}
                      name="shippingRateHandle"
                      defaultValue="$10 Delivery"
                      render={({ field, fieldState }) => (
                        <>
                          <RadioGroup {...field}>
                            {cart.deliveryGroups?.edges[0]?.node.deliveryOptions.map(
                              (shippingRate) => (
                                <FormControl key={shippingRate.handle}>
                                  <FormControlLabel
                                    value={shippingRate.handle}
                                    control={<Radio />}
                                    label={
                                      <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        width="100%"
                                      >
                                        <Box>
                                          <Typography>
                                            {shippingRate.title}
                                          </Typography>

                                          {deliveryOption ===
                                            DELIVERY_OPTIONS.NEIGHBOR_ISLAND_SHIPPING && (
                                            <Typography
                                              variant="caption"
                                              fontSize="14px"
                                              color="grey"
                                            >
                                              1-2 Bottles ($12) | 3-6 bottles
                                              ($18) | 7-12 bottles ($25)
                                            </Typography>
                                          )}

                                          {deliveryOption ===
                                            DELIVERY_OPTIONS.US_MAINLAND_SHIPPING && hasBeerItems && (
                                            <Typography
                                              variant="caption"
                                              color="grey"
                                            >
                                              If ordering 6 bottles or more,
                                              please contact us for cheaper rate
                                            </Typography>
                                          )}
                                        </Box>
                                        <Typography>
                                          {currencyFormat(
                                            shippingRate.estimatedCost.amount,
                                          )}
                                        </Typography>
                                      </Box>
                                    }
                                    disableTypography
                                    sx={{ mr: 0 }}
                                  />
                                </FormControl>
                              ),
                            )}
                          </RadioGroup>
                          {fieldState.invalid && (
                            <FormHelperText error={fieldState.invalid}>
                              Please select a delivery method.
                            </FormHelperText>
                          )}
                        </>
                      )}
                    />
                  </Box>
                </>
              ) : (
                <Alert
                  severity="error"
                  width="100%"
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 2,
                  }}
                >
                  Order minimum to your location is {currencyFormat(minimumRate)}
                </Alert>
              )}
            </> : <Alert
              severity="error"
              width="100%"
              sx={{
                alignItems: "center",
                justifyContent: "center",
                padding: 2,
              }}
            >
              <strong>Shipping not available</strong>
              <br/>
              Your order cannot be shipped to the selected address.
              Review your address to ensure it's correct and try again, or select a different address.
              Additionally, please check if there are any items (Flowers) in your cart that cannot be shipped to your address.
              You can remove those items to proceed with the checkout.
            </Alert>}
          </Box>
        )}
      </Box>

      <TextareaWithCharCount
        name="note"
        control={control}
        label="Personalized Gift Message and/or Special Instructions"
        size="small"
        rows={4}
        multiline
        fullWidth
        maxLength={150}
        value={watch("note")}
        sx={{ my: 2 }}
      />

      <BottomNav
        loading={getAvailableShippingRatesLoading || cartNoteUpdateLoading || cartSelectedDeliveryOptionsUpdateLoading || hawaiiGetLoading}
        disabled={!aboveMinimumRate || cart.deliveryGroups?.edges[0]?.node.deliveryOptions.length === 0}
      />

      {deliveryOption !== DELIVERY_OPTIONS.US_MAINLAND_SHIPPING && <>
        <Addons />

        <BottomNav
          loading={getAvailableShippingRatesLoading || cartNoteUpdateLoading || cartSelectedDeliveryOptionsUpdateLoading || hawaiiGetLoading}
          disabled={!aboveMinimumRate || cart.deliveryGroups?.edges[0]?.node.deliveryOptions.length === 0}
        />
      </>}
    </form>
  )
}
